/**
 * Shared read model for the Fridge.com Most Efficient Refrigerators leaderboard.
 *
 * Single source of truth for the page (app/data/most-efficient/page.tsx), the
 * JSON-LD schema props, the CSV route (app/data/most-efficient/csv/route.ts),
 * and the share-card image route (app/data/most-efficient/card/route.tsx), so
 * the on-page stat grid, the per-type league tables, the structured data, and
 * every download cite the SAME figures from app/data/_data/most-efficient.json.
 *
 * INTEGRITY: every number here is read directly from that JSON, which is itself
 * a frozen snapshot derived from the ENERGY STAR certified refrigeration list
 * (public U.S. EPA / DOE data). Within each appliance type, models are ranked by
 * certified annual energy use per cubic foot of total storage volume; only
 * models that publish a capacity and beat the applicable U.S. federal maximum
 * energy-use standard are eligible. Nothing is invented or narrated. A field the
 * source lacks is absent, never estimated.
 *
 * This is the NAMED-WINNER leaderboard companion to the aggregate
 * /data/energy-index benchmark (the index reports the medians and distribution;
 * this page names the individual leaders). The two never share a table.
 */
import mostEfficientData from '@/app/data/_data/most-efficient.json'

export interface WinnerRow {
  rank: number
  brand: string
  model: string
  /** Certified annual energy use, kWh/yr. */
  kwh: number
  /** Total storage volume, ft³. */
  cap: number
  /** Certified annual energy use per cubic foot, kWh/yr per ft³ (2-decimal). */
  kwh_per_ft3: number
  /** Percent below the applicable U.S. federal maximum energy-use standard. */
  pct_less_than_federal: number
  /** Carries the ENERGY STAR Most Efficient designation. */
  meets_most_efficient: boolean
}

export interface TypeBlock {
  type: string
  /** Distinct eligible models of this type. */
  n: number
  /** Eligible models of this type carrying the Most Efficient badge. */
  most_efficient_n: number
  /** The type leader's kWh/yr per ft³. */
  best_kwh_per_ft3: number
  /** Median kWh/yr per ft³ across the type's eligible models. */
  median_kwh_per_ft3: number
  winners: WinnerRow[]
}

export interface ChampionRow {
  type: string
  brand: string
  model: string
  kwh: number
  cap: number
  kwh_per_ft3: number
  pct_less_than_federal: number
  meets_most_efficient: boolean
}

export interface MostEfficientOverall {
  certified_records: number
  brands: number
  distinct_qualifying_models: number
  typed_types_total: number
  published_types: number
  below_threshold_types: number
  winners_listed: number
  most_efficient_ranked: number
  most_efficient_records: number
  champion: ChampionRow
}

export interface MostEfficientMeta {
  methodology_version: string
  as_of: string
  source_program: string
  source_authority: string
  source_url: string
  source_urls: string[]
  synthesis_note: string
  publication_min_n: number
  winners_per_type: number
  temporal_coverage: { earliest_qualified: string; latest_qualified: string }
  limitations: string[]
}

export interface BelowThresholdRow {
  type: string
  n: number
}

export const meMeta = mostEfficientData.meta as unknown as MostEfficientMeta
export const meOverall = mostEfficientData.overall as unknown as MostEfficientOverall
export const meTypes = mostEfficientData.types as unknown as TypeBlock[]
export const meBelowThreshold = mostEfficientData.below_threshold as unknown as BelowThresholdRow[]

export const mePublicationMinN = meMeta.publication_min_n ?? 30
export const meWinnersPerType = meMeta.winners_per_type ?? 10

/** Every listed winner row across all published types, flattened. */
export function allWinners(): WinnerRow[] {
  return meTypes.flatMap((t) => t.winners)
}

/** Distinct brands that place at least one model on the leaderboard. */
export function distinctWinnerBrands(): number {
  return new Set(allWinners().map((w) => w.brand)).size
}

/** Listed winner rows carrying the ENERGY STAR Most Efficient badge. */
export function badgedWinnerCount(): number {
  return allWinners().filter((w) => w.meets_most_efficient).length
}

/**
 * The single most efficient listed model in each published type (its rank-1
 * row), sorted best-first by kWh/yr per ft³ — the class-leader set drawn as the
 * "most efficient in each class" figure. Every entry is a real named winner.
 */
export function typeChampions(): Array<ChampionRow & { n: number }> {
  return meTypes
    .map((t) => {
      const top = t.winners.find((w) => w.rank === 1) ?? t.winners[0]
      return {
        type: t.type,
        brand: top.brand,
        model: top.model,
        kwh: top.kwh,
        cap: top.cap,
        kwh_per_ft3: top.kwh_per_ft3,
        pct_less_than_federal: top.pct_less_than_federal,
        meets_most_efficient: top.meets_most_efficient,
        n: t.n,
      }
    })
    .sort((a, b) => a.kwh_per_ft3 - b.kwh_per_ft3)
}

/** The published type with the most badge holders — the deepest Most Efficient bench. */
export function deepestBadgeType(): TypeBlock | null {
  if (meTypes.length === 0) return null
  return [...meTypes].sort((a, b) => b.most_efficient_n - a.most_efficient_n)[0]
}

/** Format kWh/yr per ft³ with two decimals held (spec-decimal rule). */
export function fmtPerFt3(n: number): string {
  return n.toFixed(2)
}

/** Format a whole-number kWh value with a thousands separator. */
export function fmtKwh(n: number): string {
  return n.toLocaleString('en-US')
}

/** Format a capacity figure, holding at most two decimals, no trailing zeros. */
export function fmtCap(n: number): string {
  return Number(n.toFixed(2)).toLocaleString('en-US')
}

/** 'YYYY-MM-DD' → 'January 2014'. Parsed as UTC to avoid timezone drift. */
export function formatDateLabel(date: string | null | undefined): string {
  if (!date) return 'n/a'
  const [y, m] = date.split('-').map(Number)
  if (!y || !m) return date
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Public display label for the methodology version, e.g. "v1.0". */
export const meMethodologyVersionLabel: string = (() => {
  const raw = meMeta.methodology_version ?? ''
  const core = raw.split('-')[0]
  const parts = core.split('.').filter(Boolean)
  if (parts.length >= 2) return `v${parts[0]}.${parts[1]}`
  return core ? `v${core}` : 'n/a'
})()

/**
 * Canonical FAQ set for the leaderboard, drawn from the live numbers. Shared by
 * the FAQPage JSON-LD and the on-page FAQ so the structured data always matches
 * the rendered text.
 */
/** The full-size refrigerator classes a shopper means by "refrigerator". */
const FULL_SIZE_REFRIGERATOR_TYPES = new Set(['Top Freezer', 'Bottom Freezer', 'Side-by-Side'])

/**
 * FRIDGE-1463: the answer first. Answer engines lift the page whose first
 * sentence names the leader with its figure and date; this page opened with a
 * description of the method. Quotes the dated snapshot, names the overall
 * champion and the full-size refrigerator leader, and says what the figure is.
 * Returns null when the snapshot cannot support the sentence.
 */
export function mostEfficientAnswer(): string | null {
  const o = meOverall
  const c = o?.champion
  const asOf = meMeta?.as_of
  if (!c || !asOf || !Number.isFinite(Date.parse(asOf)) || !(o.distinct_qualifying_models > 0) ||
      !Number.isFinite(c.kwh_per_ft3) || c.kwh_per_ft3 <= 0) return null
  const fullSize = typeChampions()
    .filter((t) => FULL_SIZE_REFRIGERATOR_TYPES.has(t.type) && Number.isFinite(t.kwh_per_ft3) && t.kwh_per_ft3 > 0)
    .sort((a, b) => a.kwh_per_ft3 - b.kwh_per_ft3)
  const lead = fullSize[0]
  const fullSizeClause = lead && !(lead.brand === c.brand && lead.model === c.model)
    ? ` Among full-size refrigerators, the ${lead.brand} ${lead.model} (${lead.type}) leads at ${fmtPerFt3(lead.kwh_per_ft3)} kWh per year per cubic foot.`
    : ''
  return `In the ENERGY STAR certified list Fridge.com analyzed in ${formatDateLabel(asOf)}, the ${c.brand} ${c.model} (${c.type}) has the lowest certified energy use per cubic foot of storage among ${o.distinct_qualifying_models.toLocaleString('en-US')} qualifying models, at ${fmtPerFt3(c.kwh_per_ft3)} kWh per year per cubic foot.${fullSizeClause} These are certified test figures normalized by storage volume, not measured household consumption.`
}

export function buildMostEfficientFAQs(): Array<{ question: string; answer: string }> {
  const o = meOverall
  const c = o.champion
  const badged = badgedWinnerCount()
  const brands = distinctWinnerBrands()
  return [
    {
      question: 'What is the most efficient refrigerator?',
      answer: `Ranked by certified annual energy use per cubic foot of storage, the leader across every class is the ${c.brand} ${c.model} (${c.type}), at ${fmtPerFt3(c.kwh_per_ft3)} kWh/yr per ft³ — ${fmtKwh(c.kwh)} kWh/yr across ${fmtCap(c.cap)} ft³, ${c.pct_less_than_federal}% below the U.S. federal maximum. This is a comparison within the certified set, using storage-normalized energy use; each appliance type has its own leader in the tables on this page.`,
    },
    {
      question: 'How is "most efficient" defined here?',
      answer: `Every model is ranked by certified annual energy use divided by total storage volume — kilowatt-hours per year, per cubic foot. Lower is better. It is a size-normalized measure, so a large unit that sips power can outrank a tiny one that draws fewer raw kilowatt-hours. Only models that publish a capacity and that beat the applicable U.S. federal maximum energy-use standard are eligible.`,
    },
    {
      question: 'What is the ENERGY STAR Most Efficient badge?',
      answer: `ENERGY STAR Most Efficient is a separate, top-tier designation the U.S. EPA and DOE award to a subset of already-certified models. It is offered for only some appliance types, so a model can lead its class per cubic foot without carrying the badge. ${o.most_efficient_ranked.toLocaleString('en-US')} of the ${o.distinct_qualifying_models.toLocaleString('en-US')} ranked models hold it; ${badged} of the ${o.winners_listed} named winners on this page are flagged.`,
    },
    {
      question: 'Where does the data come from?',
      answer: `The certified models and their tested annual energy use come from the ENERGY STAR residential refrigerator and freezer releases published by the U.S. EPA and DOE — ${o.certified_records.toLocaleString('en-US')} certification records across ${o.brands} brands. Fridge.com ranks them per cubic foot within each appliance type; the ranking is the Fridge.com synthesis, published under a CC BY 4.0 license.`,
    },
    {
      question: 'How is this different from the Energy-Efficiency Index?',
      answer: `The Refrigerator Energy-Efficiency Index is the benchmark: it reports the median energy use, the distribution, and the margin over the federal standard for each appliance type. This leaderboard is the roll of honor: it names the individual model winners behind those medians. Use the index to see where a type sits; use this page to see which models lead it.`,
    },
    {
      question: 'Can I buy the models on this leaderboard?',
      answer: `Not necessarily. This is an editorial-authority ranking over the full ENERGY STAR certified record, not a shopping list. Many certified models are discontinued or were never carried for sale, so a listed winner may not be available to buy. ${brands} distinct brands place at least one model on the board; browse the live Fridge.com catalog to shop what is currently stocked.`,
    },
  ]
}
